/**
 * Style Space v2 (redesign) mutations — `docs/backend/STYLE_SPACE_V2_API_GAPS.md`.
 * Duplicate keeps a client-side fallback; link / favourite are only offered when their
 * capability is on (the UI hides or toasts otherwise).
 */
import { createMutation } from '@/mutations'
import {
  createOutfit,
  duplicateOutfit,
  linkWardrobeItem,
  setOutfitFavorite,
  type CreateOutfitBody,
  type CreateOutfitResponse,
  type OutfitFavoriteResponse,
  type WardrobeLinkBody,
  type WardrobeLinkResponse,
} from '@/features/style-space/api'
import { isStyleSpaceV2ApiEnabled } from '@/features/style-space/flags'
import { styleSpaceQueriesOf } from '@/queries/style-space'

const invalidateWardrobeReaders = styleSpaceQueriesOf('wardrobe', 'wardrobe-item', 'outfits', 'capsules', 'capsule', 'style-dna')
const invalidateOutfitReaders = styleSpaceQueriesOf('outfits', 'capsules', 'capsule', 'available-items')

export interface LinkWardrobeItemVariables extends WardrobeLinkBody {
  overviewId: number
}

/** §3 */
export const useLinkWardrobeItemMutation = () =>
  createMutation<WardrobeLinkResponse, LinkWardrobeItemVariables>({
    mutationFn: async ({ overviewId, ...body }) => {
      const response = await linkWardrobeItem(overviewId, body)
      return response.data
    },
    invalidateQueries: invalidateWardrobeReaders,
  })

/** §5 */
export const useSetOutfitFavoriteMutation = () =>
  createMutation<OutfitFavoriteResponse, { outfitId: number; isFavorite: boolean }>({
    mutationFn: async ({ outfitId, isFavorite }) => {
      const response = await setOutfitFavorite(outfitId, { isFavorite })
      return response.data
    },
    invalidateQueries: invalidateOutfitReaders,
  })

export interface DuplicateOutfitVariables {
  outfitId: number
  /** Client-side fallback body while `outfit_duplicate` is off. */
  fallback: CreateOutfitBody
}

/** §6 — endpoint when the capability is on, otherwise `POST /playground/outfits` with the same items. */
export const useDuplicateOutfitMutation = () =>
  createMutation<CreateOutfitResponse, DuplicateOutfitVariables>({
    mutationFn: async ({ outfitId, fallback }) => {
      const response = isStyleSpaceV2ApiEnabled('outfit_duplicate')
        ? await duplicateOutfit(outfitId)
        : await createOutfit(fallback)
      return response.data
    },
    invalidateQueries: invalidateOutfitReaders,
  })
